/**
 * Tiny ESC/POS printer driver for 58/80mm thermal printers via Web Serial API.
 * Browser support: Chrome / Edge / Opera (desktop & Android). HTTPS required.
 *
 * Usage:
 *   const printer = await connectPrinter();         // pairs the device (user prompt)
 *   await printer.printReceipt(receiptData);        // sends ESC/POS bytes
 *
 * Falls back to window.print() of a hidden 80mm-styled <div> when Web Serial
 * is not available — callers just check `printer.kind`.
 */

const ESC = 0x1b;
const GS = 0x1d;

const CMD = {
  INIT: [ESC, 0x40],
  ALIGN_LEFT: [ESC, 0x61, 0],
  ALIGN_CENTER: [ESC, 0x61, 1],
  ALIGN_RIGHT: [ESC, 0x61, 2],
  BOLD_ON: [ESC, 0x45, 1],
  BOLD_OFF: [ESC, 0x45, 0],
  SIZE_NORMAL: [GS, 0x21, 0],
  SIZE_LARGE: [GS, 0x21, 0x11],
  FEED: [0x0a],
  CUT: [GS, 0x56, 0x01],
};

function encode(text) {
  return new TextEncoder().encode(text);
}

function bytes(...arrays) {
  const total = arrays.reduce((s, a) => s + a.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const a of arrays) {
    out.set(a instanceof Uint8Array ? a : new Uint8Array(a), off);
    off += a.length;
  }
  return out;
}

const fmtRWF = (n) => new Intl.NumberFormat('en-RW', { maximumFractionDigits: 0 }).format(n || 0);

function buildReceiptBytes(r) {
  const COLS = 32; // 58mm printers fit ~32 cols; 80mm fits ~48 — keep 32 for safety
  const line = '-'.repeat(COLS) + '\n';
  const left = (s) => String(s).padEnd(COLS) + '\n';
  const row = (l, rt) => {
    const r2 = String(rt);
    const space = Math.max(1, COLS - l.length - r2.length);
    return l + ' '.repeat(space) + r2 + '\n';
  };

  return bytes(
    CMD.INIT,
    CMD.ALIGN_CENTER,
    CMD.SIZE_LARGE, encode('Bison Shop\n'), CMD.SIZE_NORMAL,
    encode('Kigali, Rwanda\n'),
    encode(`Receipt #${r.order_number}\n`),
    encode(`${new Date(r.created_at).toLocaleString()}\n`),
    encode(line),
    CMD.ALIGN_LEFT,
    ...r.items.flatMap(it => [
      encode(left(`${it.product_name} x ${it.quantity}`)),
      encode(row(`  @ RWF ${fmtRWF(it.unit_price)}`, `RWF ${fmtRWF(it.line_total)}`)),
    ]),
    encode(line),
    encode(row('Subtotal', `RWF ${fmtRWF(r.subtotal)}`)),
    ...(r.discount > 0 ? [encode(row('Discount', `- RWF ${fmtRWF(r.discount)}`))] : []),
    CMD.BOLD_ON, encode(row('TOTAL', `RWF ${fmtRWF(r.total)}`)), CMD.BOLD_OFF,
    encode(row('Paid via', String(r.payment_method).replace(/_/g, ' '))),
    ...(r.change != null ? [encode(row('Change', `RWF ${fmtRWF(r.change)}`))] : []),
    encode(line),
    CMD.ALIGN_CENTER,
    encode(`Served by ${r.pos_cashier_name || 'cashier'}\n`),
    encode('Thank you!\n\n\n'),
    CMD.CUT,
  );
}

let connected = null;

export async function connectPrinter() {
  if (!('serial' in navigator)) {
    return { kind: 'unsupported', error: 'Web Serial not supported in this browser. Use Chrome / Edge on a desktop or Android.' };
  }
  try {
    const port = await navigator.serial.requestPort();
    await port.open({ baudRate: 9600 });
    connected = port;
    return { kind: 'serial', port };
  } catch (err) {
    return { kind: 'error', error: err.message || 'Could not open printer port' };
  }
}

export async function disconnectPrinter() {
  try { if (connected) { await connected.close(); connected = null; } } catch {}
}

export function isPrinterConnected() { return !!connected; }

export async function printReceipt(receipt) {
  if (!connected) throw new Error('Printer not connected. Click "Connect printer" first.');
  const data = buildReceiptBytes(receipt);
  const writer = connected.writable.getWriter();
  try { await writer.write(data); } finally { writer.releaseLock(); }
}

/** Browser-print fallback — uses the existing receipt dialog with @media print rules. */
export function browserPrintFallback() {
  window.print();
}
